/**
 * The chrome that never changes: sprite, two navigations, the account row, the toast — and, in the
 * middle of it, whichever screen is showing (dc.html:51-550).
 *
 * ONE NAVIGATION ARRAY. `buildNavItems` is called exactly once here and the result handed to both
 * `<Sidebar>` and `<BottomNav>`. The prototype's first shape had eight hand-written buttons in two
 * places with the icon geometry pasted into both; every new destination then had to be added twice
 * or the phone and the desktop quietly disagreed about where you were.
 *
 * THE ACCOUNT ROW IS NOT IN THE PROTOTYPE, and it is not decoration. The prototype had no accounts
 * at all — one dog, no session, an avatar button that raised a toast saying account settings were
 * coming. A real portal on a real phone needs a way out of the session (these are shared family
 * devices) and, for the 9 households with two dogs, a way to say which dog is on screen. Both live
 * in one row at the top of `<main>`, at every width, rather than in the sidebar — a control that
 * only exists above 1120px is a control most of these clients will never see.
 */

import { useState, type ReactElement } from 'react'

import { signOut } from '../api/client'
import { PHONE_DISPLAY, PHONE_HREF } from '../model/contact'
import { IconSprite } from '../model/icons'
import { buildNavItems } from '../model/nav'
import { useMe, usePortal, useSelectedDog } from '../state/portal'
import { HomeScreen } from '../screens/HomeScreen'
import { PrivacyScreen } from '../screens/PrivacyScreen'
import { ProfileScreen } from '../screens/ProfileScreen'
import { RemindersScreen } from '../screens/RemindersScreen'
import { ReviewScreen } from '../screens/ReviewScreen'
import { BottomNav } from './BottomNav'
import { DogSwitcher } from './DogSwitcher'
import { Sidebar } from './Sidebar'
import { Toast } from './Toast'

export function AppShell(): ReactElement {
  const { state, go, showToast } = usePortal()
  const me = useMe()
  const dog = useSelectedDog()

  /*
    The attention dot is deliberately OFF.

    In the prototype it tracked the wizard's own `done` latch. Against real data the only candidate
    signal is "this record has gaps" — and the care dates were added to the JotForm so recently
    that essentially every one of the 199 records has them, so the dot would be lit for everybody.
    A badge that is always on is not a badge. It comes back when the record carries an outstanding
    review the client can actually clear.
  */
  const navItems = buildNavItems(state.screen, false)

  const dogs = me.data?.dogs ?? []
  const privacyNote =
    dog === null
      ? 'Your record is private. Tell us at your next visit if anything needs changing.'
      : `${dog.name}’s information is private. Tell us at your next visit if anything needs changing.`

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Once, above both navigations — every <use href="#ft-ic-…"> in the page resolves here. */}
      <IconSprite />

      <StaffBanner />

      <Sidebar items={navItems} onNavigate={go} privacyNote={privacyNote} />

      <main className="ft-main">
        <AccountRow onSignOutFailure={showToast} />

        {/*
          The privacy notice is exempt from the no-dogs card. A household with no dog attached still
          has personal data on file and still has Article 13 rights over it — routing them to "no
          dog on this record yet" instead of the notice would deny the one screen that is about
          them rather than about the dog.
        */}
        {me.status === 'ready' && dogs.length === 0 && state.screen !== 'privacy' ? (
          <NoDogsCard />
        ) : (
          <ScreenView />
        )}
      </main>

      <BottomNav items={navItems} onNavigate={go} />

      <Toast />
    </div>
  )
}

/**
 * The screen switch.
 *
 * `ScreenId` is a literal union, so there is no default branch and no "unknown screen" state: the
 * prototype could render an empty `<main>` with the navigation still drawn around it, and the only
 * symptom was a blank page. Add a screen to `ScreenId` and this stops compiling.
 */
function ScreenView(): ReactElement {
  const { state } = usePortal()
  switch (state.screen) {
    case 'home':
      return <HomeScreen />
    case 'profile':
      return <ProfileScreen />
    case 'reminders':
      return <RemindersScreen />
    case 'review':
      return <ReviewScreen />
    case 'privacy':
      // No back control: the navigation is still on screen here, unlike the signed-out case.
      return <PrivacyScreen onBack={null} />
  }
}

/**
 * A permanent marker that this is somebody else's record.
 *
 * Renders nothing at all in a client session — the customer must never see a trace of it. For
 * staff it is deliberately the loudest thing on the page and it does not scroll away: this screen
 * is otherwise pixel-identical to what the client sees, which is the entire point of it and also
 * the entire risk. Somebody who forgets whose record is open can read another family's details
 * down the phone to the wrong person.
 */
function StaffBanner(): ReactElement | null {
  const { viewingAs } = usePortal()
  if (viewingAs === null) return null

  return (
    <div
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '8px 16px',
        background: 'var(--color-accent-800)',
        color: 'var(--color-bg)',
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      <span>
        Staff view — you are looking at{' '}
        <strong>{viewingAs.householdName ?? 'a client'}</strong>’s record, not your own.
      </span>
      <button
        type="button"
        onClick={viewingAs.onExit}
        style={{
          marginLeft: 'auto',
          border: 0,
          background: 'none',
          font: 'inherit',
          color: 'inherit',
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: '4px 0',
        }}
      >
        Back to all dogs
      </button>
    </div>
  )
}

interface AccountRowProps {
  readonly onSignOutFailure: (message: string) => void
}

function AccountRow({ onSignOutFailure }: AccountRowProps): ReactElement {
  const me = useMe()
  const { viewingAs } = usePortal()
  const [leaving, setLeaving] = useState(false)

  const email = me.data?.owner.email ?? null

  async function onSignOut(): Promise<void> {
    if (leaving) return
    setLeaving(true)

    const result = await signOut()
    if (!result.ok) {
      /*
        A sign-out that failed must SAY so. The session cookie is http-only, so JavaScript cannot
        clear it — pretending to have signed out and navigating away would leave the next person to
        pick up the phone one tap from someone else's dog record.
      */
      setLeaving(false)
      onSignOutFailure(result.error.message)
      return
    }

    // A full navigation rather than a state reset, so nothing about the previous household — the
    // cached record, the selected dog, the search box — survives in memory on a shared device.
    window.location.replace('/')
  }

  return (
    <div style={{ marginBottom: '18px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <DogSwitcher />

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
          {email === null ? null : (
            <span className="text-muted" style={{ fontSize: '12.5px', overflowWrap: 'anywhere' }}>
              {email}
            </span>
          )}
          {/*
            Staff are looking at somebody else's record, so "Sign out" would be wrong twice: it
            would try to clear a client cookie they do not hold, and it is not the action they
            want. Their way out is back to the dog list.
          */}
          {viewingAs === null ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onSignOut()}
              disabled={leaving}
              aria-busy={leaving}
              style={{ minHeight: '44px', fontSize: '13px' }}
            >
              {leaving ? 'Signing out…' : 'Sign out'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={viewingAs.onExit}
              style={{ minHeight: '44px', fontSize: '13px' }}
            >
              Back to all dogs
            </button>
          )}
        </div>
      </div>

      <LoadState />
    </div>
  )
}

/**
 * A quiet second line for the two states that are neither "loading" nor "broken".
 *
 * Mounted permanently and emptied rather than unmounted, for the same reason as the toast: a live
 * region has to exist before its text changes or the change is not reliably announced. `min-height`
 * holds the space so the page does not jump when a refresh starts.
 */
function LoadState(): ReactElement {
  const me = useMe()

  const text = me.refreshing
    ? 'Checking for anything new…'
    : /*
        The record is on screen AND a reload failed. Showing the last good copy with a note is more
        honest than either blanking the page or pretending it is current — `MeResult` keeps `data`
        and `error` at the same time precisely so this state can be told apart from a first-load
        failure, which never reaches the shell at all.
      */
      me.data && me.error
      ? 'Showing the record we last loaded — we could not check for updates just now.'
      : ''

  return (
    <p
      role="status"
      className="text-muted"
      style={{ margin: '6px 4px 0', fontSize: '12.5px', minHeight: '16px', lineHeight: 1.35 }}
    >
      {text}
    </p>
  )
}

/**
 * A signed-in household with no dog attached.
 *
 * Rare, but real: the session is keyed on the customer record, and the dog rows come from a
 * separate JotForm join that can legitimately have found nothing yet. Rendering the four screens
 * against no dog would give a page of empty cards and a "Coming up" heading over nothing; this
 * says what has happened and what to do about it.
 */
function NoDogsCard(): ReactElement {
  return (
    <section
      className="card elev-sm"
      style={{
        alignItems: 'center',
        textAlign: 'center',
        gap: 0,
        padding: '36px 26px',
        animation: 'ft-rise .4s ease both',
      }}
    >
      <h1 style={{ margin: '0 0 6px', fontSize: '26px' }}>No dog on this record yet</h1>
      <p className="text-muted" style={{ maxWidth: '44ch', margin: 0, fontSize: '14px', lineHeight: 1.55 }}>
        You are signed in, but we have not yet linked a dog to your account. That is almost always
        something we can put right in a moment — please mention it at your next visit, or ring us on{' '}
        <a href={PHONE_HREF} style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {PHONE_DISPLAY}
        </a>
        , and we will connect the record up.
      </p>
    </section>
  )
}
